import React, { useEffect } from 'react';
import Link from '@dashboard/components/Link';
import ZapierLogo from './zapier.svg';
import RewardfulLogo from './rewardful.svg';
import ToltLogo from './tolt.png';
import Button from '@dashboard/components/Button';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import Features from '@dashboard/enums/features';
import GoogleSheetIcon from '@dashboard/icons/GoogleSheetIcon';
import WebhookIcon from '@dashboard/icons/WebhookIcon';
import PageHeader from '@dashboard/components/PageHeader';
import {
  Item,
  ItemMedia,
  ItemContent,
  ItemTitle,
  ItemDescription,
  ItemAction,
} from '@dashboard/components/ui/item';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sellerGoogleKey, sellerSlackKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';
import { toast } from '@dashboard/context/ToastContext';
import Dialog from '@dashboard/components/Dialog';
import { CheckIcon } from '@heroicons/react/24/outline';
import { appUrl } from '@dashboard/services/env';
import { stringify } from 'query-string';
import useSellerQueries from '@/dashboard/hooks/queries/useSellerQueries';
import { prompt } from '@dashboard/lib/prompt';
import SlackIcon from '@dashboard/icons/SlackIcon';
import SettingsContainer from '@/dashboard/components/SettingsContainer';

const SettingsIntegrations = () => {
  const { seller } = useAuth();

  return (
    <div className="p-4 space-y-4">
      <PageHeader
        title="Integrations"
        description={
          <>
            Integrate Checkout Page with other apps through webhooks, Zapier and more.{' '}
            <Link href="/docs/articles/integrations" color="default" blank>
              Learn more
            </Link>
          </>
        }
      />

      <div className="mb-32">
        <SettingsContainer size="4xl">
          <div>
            <CanUseFeature feature={Features.GOOGLE_SHEETS}>
              <IntegrationRow
                logo={<GoogleSheetIcon className="h-9 w-auto" />}
                title="Google Sheets"
                description={
                  <>
                    Automatically add new orders to a Google Sheet.{' '}
                    <Link to="/docs/articles/google-sheets-integration" target="_blank">
                      Learn more
                    </Link>
                  </>
                }
                status={<GoogleSheetsStatus />}
              >
                <GoogleSheetsOAuth />
              </IntegrationRow>
            </CanUseFeature>

            {seller?.labs?.slack && (
              <CanUseFeature feature={Features.SLACK}>
                <IntegrationRow
                  logo={<SlackIcon className="h-9 w-auto" />}
                  title="Slack"
                  description={
                    <>
                      Get notified about new orders in your Slack workspace.{' '}
                      <Link to="/docs/articles/slack" target="_blank">
                        Learn more
                      </Link>
                    </>
                  }
                  status={<SlackStatus />}
                >
                  <SlackOAuth />
                </IntegrationRow>
              </CanUseFeature>
            )}

            <CanUseFeature feature={Features.WEBHOOKS}>
              <IntegrationRow
                logo={<WebhookIcon className="h-9 w-auto" />}
                title="Webhooks"
                description={
                  <>
                    Configure webhook endpoints to receive real-time updates from Checkout Page.{' '}
                    <Link to="/docs/articles/webhooks" target="_blank">
                      Learn more
                    </Link>
                  </>
                }
              >
                <Button to="/settings/webhooks" appearance="primary" size="small">
                  Manage webhooks
                </Button>
              </IntegrationRow>
            </CanUseFeature>

            <CanUseFeature feature={Features.ZAPIER}>
              <IntegrationRow
                logo={
                  <img
                    src={ZapierLogo}
                    alt="Zapier"
                    className="max-h-9 max-w-full object-contain"
                  />
                }
                title="Zapier"
                description={
                  <>
                    Connect with thousands of apps to automate your business tasks with our Zapier
                    integration. For example, add new customers to your CRM or to a mailing list
                    when you receive a new payment. No coding needed.{' '}
                    <Link to="/docs/articles/integrate-with-zapier" target="_blank">
                      Learn more
                    </Link>
                  </>
                }
              >
                <Button
                  to="https://zapier.com/apps/checkout-page/integrations"
                  target="_blank"
                  appearance="light"
                  size="small"
                  external
                >
                  Visit Zapier
                </Button>
              </IntegrationRow>
            </CanUseFeature>

            <IntegrationRow
              logo={
                <img
                  src={RewardfulLogo}
                  alt="Rewardful"
                  className="max-h-9 max-w-full object-contain"
                />
              }
              title="Rewardful"
              description={
                <>
                  Rewardful is an easy way to setup affiliate and referral programs. Connect your
                  checkout pages with Rewardful to track referrals and commissions.{' '}
                  <Link to="/docs/articles/rewardful-integration" target="_blank">
                    Learn more
                  </Link>
                </>
              }
            >
              <Button
                to="https://www.getrewardful.com/?via=checkoutpage"
                target="_blank"
                appearance="light"
                size="small"
                external
              >
                Visit Rewardful
              </Button>
            </IntegrationRow>

            <IntegrationRow
              logo={<img src={ToltLogo} alt="Tolt" className="max-h-9 max-w-full object-contain" />}
              title="Tolt"
              description={
                <>
                  Tolt is an all-in-one affiliate marketing software for SaaS startups. Launch your
                  own affiliate program and track referrals seamlessly.{' '}
                  <Link to="/docs/articles/tolt-integration" target="_blank">
                    Learn more
                  </Link>
                </>
              }
              status={<ToltStatus />}
            >
              <ToltEnableDisable />
            </IntegrationRow>
          </div>
        </SettingsContainer>
      </div>
    </div>
  );
};

function IntegrationRow({ logo, title, description, status, children }) {
  return (
    <Item className="flex-wrap items-center gap-4 border-b border-gray-100 dark:border-border px-0 py-5 last:border-b-0 sm:flex-nowrap">
      <ItemMedia className="h-12 w-16 rounded-lg bg-gray-50 dark:bg-white p-2 sm:h-16 sm:w-24 sm:p-3">
        {logo}
      </ItemMedia>
      <ItemContent className="min-w-0 flex-1 gap-1">
        <ItemTitle className="text-[15px] font-semibold text-gray-900 dark:text-foreground">
          {title}
        </ItemTitle>
        <ItemDescription className="whitespace-normal text-sm leading-relaxed text-gray-600 dark:text-muted-foreground">
          {description}
        </ItemDescription>
        {status}
      </ItemContent>
      <ItemAction className="self-center max-sm:basis-full max-sm:pl-20 sm:pl-4">
        {children}
      </ItemAction>
    </Item>
  );
}

function ConnectedStatus({ children }) {
  return (
    <div className="mt-0.5 flex items-center gap-x-1.5 text-sm text-gray-500 dark:text-muted-foreground">
      <CheckIcon className="h-4 w-4 shrink-0 text-green-600 dark:text-green-500" />
      <span className="min-w-0 break-all">{children}</span>
    </div>
  );
}

function ToltEnableDisable() {
  const { seller } = useAuth();
  const { updateSeller } = useSellerQueries();

  const isEnabled = seller.integrations?.tolt?.enabled;
  const publicId = seller.integrations?.tolt?.publicId;

  const handleToggle = async () => {
    if (isEnabled) {
      // Disable the integration
      updateSeller({ integrations: { tolt: { enabled: false } } });
    } else {
      // Enable the integration - prompt for program ID
      try {
        const enteredPublicId = await prompt('Enter your Tolt Public ID', {
          placeholder: 'Public ID (pk_xxx)',
          description: (
            <div>
              Grab your public ID (data-tolt ID) in the{' '}
              <Link to="https://app.tolt.io/settings?tab=integrations" external target="_blank">
                Tolt dashboard
              </Link>
            </div>
          ),
          confirmText: 'Enable',
          cancelText: 'Cancel',
          value: publicId,
        });

        if (enteredPublicId && enteredPublicId.trim()) {
          updateSeller({
            integrations: {
              tolt: {
                enabled: true,
                publicId: enteredPublicId.trim(),
              },
            },
          });
        }
      } catch (error) {
        // User cancelled or error occurred
        console.log('Tolt integration setup cancelled');
      }
    }
  };

  return (
    <Button onClick={handleToggle} appearance={isEnabled ? 'secondary' : 'primary'} size="small">
      {isEnabled ? 'Disable' : 'Enable'}
    </Button>
  );
}

function ToltStatus() {
  const { seller } = useAuth();
  const tolt = seller.integrations?.tolt;

  if (!tolt?.enabled || !tolt?.publicId) {
    return null;
  }

  return (
    <ConnectedStatus>
      Enabled —{' '}
      <Link to="https://app.tolt.io/program-settings" target="_blank" external>
        {tolt.publicId}
      </Link>
    </ConnectedStatus>
  );
}

function GoogleSheetsStatus() {
  const { sellerId, token } = useAuth();

  const { data: existingAccount } = useQuery({
    queryKey: sellerGoogleKey(sellerId),
    queryFn: () => fetcher(`/oauth/google?sellerId=${sellerId}`, token),
    enabled: !!sellerId,
    retry: false,
  });

  if (!existingAccount) {
    return null;
  }

  return (
    <ConnectedStatus>
      {existingAccount.config?.email ? (
        <>
          Connected to:{' '}
          <span className="font-medium text-gray-700 dark:text-muted-foreground">
            {existingAccount.config.email}
          </span>
        </>
      ) : (
        'Connected to Google'
      )}
    </ConnectedStatus>
  );
}

function GoogleSheetsOAuth() {
  const { sellerId, token } = useAuth();
  const queryClient = useQueryClient();
  const [showDisconnectModal, setShowDisconnectModal] = React.useState(false);

  const { data: existingAccount, isLoading } = useQuery({
    queryKey: sellerGoogleKey(sellerId),
    queryFn: () => fetcher(`/oauth/google?sellerId=${sellerId}`, token),
    enabled: !!sellerId,
    retry: false,
  });

  const disconnectMutation = useMutation({
    mutationFn: () =>
      fetcher(`/oauth/google?sellerId=${sellerId}`, token, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.setQueryData(sellerGoogleKey(sellerId), null);
      queryClient.invalidateQueries({ queryKey: sellerGoogleKey(sellerId) });
      toast({
        title: 'Successfully disconnected Google account',
        type: 'success',
      });
      setShowDisconnectModal(false);
    },
    onError: () => {
      toast({
        title: 'Failed to disconnect Google account',
        type: 'error',
      });
    },
  });

  // creates oauth record from code
  const createOAuthMutation = useMutation({
    mutationFn: code => {
      return fetcher(`/oauth/google`, token, {
        method: 'POST',
        body: JSON.stringify({
          code,
          sellerId: sellerId,
          redirectURI: `${appUrl}/dashboard/integrations/google/callback`,
        }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sellerGoogleKey(sellerId) });
      toast({
        title: 'Successfully connected Google account',
        type: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Failed to connect Google account',
        type: 'error',
      });
    },
  });

  function connectNewAccount() {
    const endpointUrl = `https://accounts.google.com/o/oauth2/v2/auth?${stringify({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: `${appUrl}/dashboard/integrations/google/callback`,
      access_type: 'offline',
      response_type: 'code',
      prompt: 'consent',
      scope: [
        'https://www.googleapis.com/auth/spreadsheets',
        'https://www.googleapis.com/auth/userinfo.email',
      ].join(' '),
    })}`;

    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2.5;

    window.open(
      endpointUrl,
      'popup',
      `popup=true,width=${width},height=${height},left=${left},top=${top}`,
    );
  }

  useEffect(() => {
    const messageHandler = event => {
      if (event.origin !== appUrl) return;

      const { type, code } = event.data;

      if (type === 'google-sheets-oauth-callback' && code) {
        createOAuthMutation.mutate(code);
      }
    };

    window.addEventListener('message', messageHandler);

    return () => window.removeEventListener('message', messageHandler);
  }, []);

  if (isLoading) {
    return null;
  }

  if (!existingAccount) {
    return (
      <>
        <Button appearance="secondary" size="small" onClick={connectNewAccount}>
          Connect Google Account
        </Button>
      </>
    );
  }

  return (
    <>
      <Button appearance="secondary" size="small" onClick={() => setShowDisconnectModal(true)}>
        Disconnect
      </Button>

      <Dialog
        title="Disconnect Google Account"
        open={showDisconnectModal}
        onCancel={() => setShowDisconnectModal(false)}
        cancelButton={{
          label: 'Cancel',
          appearance: 'light',
        }}
        confirmButton={{
          label: 'Disconnect account',
          appearance: 'primary',
          onClick: () => disconnectMutation.mutateAsync(),
          loading: disconnectMutation.isLoading,
        }}
      >
        <div className="text-gray-800 dark:text-foreground">
          <p className="mb-2">
            Are you sure you want to disconnect your Google account? This will:
          </p>
          <ul className="list-disc ml-6 [&>li::marker]:text-gray-400 pl-4 space-y-0.5">
            <li className="pl-2 -ml-6">Stop sending new updates to spreadsheets</li>
            <li className="pl-2 -ml-6">Remove access to spreadsheets</li>
          </ul>
        </div>
      </Dialog>
    </>
  );
}

function SlackStatus() {
  const { sellerId, token } = useAuth();

  const { data: existingAccount } = useQuery({
    queryKey: sellerSlackKey(sellerId),
    queryFn: () => fetcher(`/oauth/slack?sellerId=${sellerId}`, token),
    enabled: !!sellerId,
    retry: false,
  });

  if (!existingAccount) {
    return null;
  }

  return (
    <ConnectedStatus>
      {existingAccount.config?.teamName ? (
        <>
          Connected to:{' '}
          <span className="font-medium text-gray-700 dark:text-muted-foreground">
            {existingAccount.config.teamName}
          </span>
        </>
      ) : (
        'Connected to Slack'
      )}
    </ConnectedStatus>
  );
}

function SlackOAuth() {
  const { sellerId, token } = useAuth();
  const queryClient = useQueryClient();
  const [showDisconnectModal, setShowDisconnectModal] = React.useState(false);

  const { data: existingAccount, isLoading } = useQuery({
    queryKey: sellerSlackKey(sellerId),
    queryFn: () => fetcher(`/oauth/slack?sellerId=${sellerId}`, token),
    enabled: !!sellerId,
    retry: false,
  });

  const disconnectMutation = useMutation({
    mutationFn: () =>
      fetcher(`/oauth/slack?sellerId=${sellerId}`, token, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.setQueryData(sellerSlackKey(sellerId), null);
      queryClient.invalidateQueries({ queryKey: sellerSlackKey(sellerId) });
      toast({
        title: 'Successfully disconnected Slack workspace',
        type: 'success',
      });
      setShowDisconnectModal(false);
    },
    onError: () => {
      toast({
        title: 'Failed to disconnect Slack workspace',
        type: 'error',
      });
    },
  });

  // creates oauth record from code
  const createOAuthMutation = useMutation({
    mutationFn: code => {
      return fetcher(`/oauth/slack`, token, {
        method: 'POST',
        body: JSON.stringify({
          code,
          sellerId: sellerId,
          redirectURI: `${appUrl}/dashboard/integrations/slack/callback`,
        }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sellerSlackKey(sellerId) });
      toast({
        title: 'Successfully connected Slack workspace',
        type: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Failed to connect Slack workspace',
        type: 'error',
      });
    },
  });

  function connectNewAccount() {
    const endpointUrl = `https://slack.com/oauth/v2/authorize?${stringify({
      client_id: process.env.REACT_APP_SLACK_CLIENT_ID,
      redirect_uri: `${appUrl}/dashboard/integrations/slack/callback`,
      scope: ['incoming-webhook', 'chat:write', 'channels:read', 'groups:read'].join(','),
    })}`;

    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2.5;

    window.open(
      endpointUrl,
      'popup',
      `popup=true,width=${width},height=${height},left=${left},top=${top}`,
    );
  }

  useEffect(() => {
    const messageHandler = event => {
      if (event.origin !== appUrl) return;

      const { type, code } = event.data;

      if (type === 'slack-oauth-callback' && code) {
        createOAuthMutation.mutate(code);
      }
    };

    window.addEventListener('message', messageHandler);

    return () => window.removeEventListener('message', messageHandler);
  }, []);

  if (isLoading) {
    return null;
  }

  if (!existingAccount) {
    return (
      <>
        <Button appearance="secondary" size="small" onClick={connectNewAccount}>
          Connect Slack workspace
        </Button>
      </>
    );
  }

  return (
    <>
      <Button appearance="secondary" size="small" onClick={() => setShowDisconnectModal(true)}>
        Disconnect
      </Button>

      <Dialog
        title="Disconnect Slack workspace"
        open={showDisconnectModal}
        onCancel={() => setShowDisconnectModal(false)}
        cancelButton={{
          label: 'Cancel',
          appearance: 'light',
        }}
        confirmButton={{
          label: 'Disconnect workspace',
          appearance: 'primary',
          onClick: () => disconnectMutation.mutateAsync(),
          loading: disconnectMutation.isLoading,
        }}
      >
        <div className="text-gray-800 dark:text-foreground">
          <p className="mb-2">
            Are you sure you want to disconnect your Slack workspace? This will stop sending new
            notifications to Slack.
          </p>
        </div>
      </Dialog>
    </>
  );
}

export default SettingsIntegrations;
